import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { MOVE_TYPES } from '../data/mockData';
import { Search, ExternalLink, Plus, Zap, Grid, FolderTree } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import ConditioningSession from './ConditioningSession';
import MobilitySession from './MobilitySession';
import TreeViewEncyclopedia from './TreeViewEncyclopedia';

const EncyclopediaView = ({ onAddMove, onAddCombo, onEditCombo, onEditMove }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const { user, activeCrewId, shareContentToCrew, unshareContent, isContentSharedToCrew, setMoveSignature, setComboSignature, getMyCrews, toggleContentShare } = useAuth();
  const moves = user?.moves || [];
  const [selectedMove, setSelectedMove] = useState(null);
  const [showStretch, setShowStretch] = useState(false);
  const [showConditioning, setShowConditioning] = useState(false);
  const [showMobility, setShowMobility] = useState(false);
  const { arsenal, learning, combos, addToArsenal, addToLearning, removeFromArsenal, removeFromLearning, getLearningStats } = useAuth();
  
  // Debug: Log state changes
  useEffect(() => {
    console.log('🎨 EncyclopediaView - Arsenal updated:', arsenal);
    console.log('🎨 EncyclopediaView - Learning updated:', learning);
  }, [arsenal, learning]);
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') return document.body.classList.contains('light') ? 'light' : 'dark';
    return 'dark';
  });
  const [moveStatusFilter, setMoveStatusFilter] = useState('all'); // all, arsenal, learning, none
  const [contentFilter, setContentFilter] = useState('all'); // all, moves, combos
  const [signatureFilter, setSignatureFilter] = useState('all'); // all, signature, non-signature
  const [viewMode, setViewMode] = useState('grid'); // grid, tree

  useEffect(() => {
    const observer = new MutationObserver(() => setTheme(document.body.classList.contains('light') ? 'light' : 'dark'));
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Combine moves and combos for display
  const allContent = [
    ...moves.map(move => ({ ...move, contentType: 'move', isBasic: !!move.isBasic })),
    ...combos.map(combo => ({ ...combo, contentType: 'combo' }))
  ];

  const filteredContent = allContent.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (item.origin && item.origin.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    let matchesType = true;
    if (item.contentType === 'move') {
      matchesType = filterType === 'all' || item.type === filterType;
    } else {
      // For combos, check if any of their moves match the filter
      matchesType = filterType === 'all' || item.moves.some(move => move.type === filterType);
    }
    
    let matchesStatus = true;
    if (item.contentType === 'move') {
      if (moveStatusFilter === 'arsenal') matchesStatus = arsenal.includes(item.id);
      else if (moveStatusFilter === 'learning') matchesStatus = learning.includes(item.id);
      else if (moveStatusFilter === 'none') matchesStatus = !arsenal.includes(item.id) && !learning.includes(item.id);
    } else {
      // For combos, they're always considered as part of arsenal
      matchesStatus = moveStatusFilter === 'all' || moveStatusFilter === 'arsenal';
    }
    
    let matchesContent = true;
    if (contentFilter === 'moves') matchesContent = item.contentType === 'move';
    else if (contentFilter === 'combos') matchesContent = item.contentType === 'combo';
    
    // Signature filter (global flag sur item.isSignature, sinon false)
    let matchesSignature = true;
    if (signatureFilter === 'signature') matchesSignature = !!item.isSignature;
    else if (signatureFilter === 'non-signature') matchesSignature = !item.isSignature;
    return matchesSearch && matchesType && matchesStatus && matchesContent && matchesSignature;
  });

  const getTypeColor = (type) => {
    const colors = {
      toprock: 'bg-blue-100 text-blue-800 border-blue-200',
      footwork: 'bg-green-100 text-green-800 border-green-200',
      powermoves: 'bg-red-100 text-red-800 border-red-200',
      freezes: 'bg-purple-100 text-purple-800 border-purple-200',
      transitions: 'bg-orange-100 text-orange-800 border-orange-200'
    };
    return colors[type] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  return (
    <div className="space-y-6">
      {/* View Mode Toggle - Always visible */}
      <div className="flex justify-end">
        <div className="flex border rounded-lg">
          <Button
            variant={viewMode === 'grid' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('grid')}
            className="rounded-r-none"
          >
            <Grid className="w-4 h-4" />
          </Button>
          <Button
            variant={viewMode === 'tree' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('tree')}
            className="rounded-l-none"
          >
            <FolderTree className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {viewMode === 'tree' ? (
        <TreeViewEncyclopedia 
          onAddMove={onAddMove}
          onAddCombo={onAddCombo}
          onEditCombo={onEditCombo}
          onEditMove={onEditMove}
        />
      ) : (
        <>
          {/* Header - Only visible in grid view */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Breaking Encyclopedia
              </h2>
              <p className="text-muted-foreground mt-1">
                Discover and explore {moves.length} breaking moves and {combos.length} combos
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button 
                onClick={onAddMove} 
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-200"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Move
              </Button>
              <Button 
                onClick={onAddCombo} 
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 transform hover:scale-105 transition-all duration-200"
              >
                <Zap className="w-4 h-4 mr-2" />
                Add Combo
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4 items-center mb-4">
        <div className="flex gap-2">
          <Button
            variant={moveStatusFilter === 'all' ? 'default' : 'outline'}
            onClick={() => setMoveStatusFilter('all')}
            className="px-3 py-1 text-xs"
          >
            All
          </Button>
          <Button
            variant={moveStatusFilter === 'arsenal' ? 'default' : 'outline'}
            onClick={() => setMoveStatusFilter('arsenal')}
            className="px-3 py-1 text-xs"
          >
            Arsenal
          </Button>
          <Button
            variant={moveStatusFilter === 'learning' ? 'default' : 'outline'}
            onClick={() => setMoveStatusFilter('learning')}
            className="px-3 py-1 text-xs"
          >
            Learning
          </Button>
          <Button
            variant={moveStatusFilter === 'none' ? 'default' : 'outline'}
            onClick={() => setMoveStatusFilter('none')}
            className="px-3 py-1 text-xs"
          >
            Without label
          </Button>
        </div>
        <div className="flex gap-2">
          <Button
            variant={contentFilter === 'all' ? 'default' : 'outline'}
            onClick={() => setContentFilter('all')}
            className="px-3 py-1 text-xs"
          >
            All
          </Button>
          <Button
            variant={contentFilter === 'moves' ? 'default' : 'outline'}
            onClick={() => setContentFilter('moves')}
            className="px-3 py-1 text-xs"
          >
            Moves
          </Button>
          <Button
            variant={contentFilter === 'combos' ? 'default' : 'outline'}
            onClick={() => setContentFilter('combos')}
            className="px-3 py-1 text-xs"
          >
            Combos
          </Button>
        </div>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search moves or origins..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 focus:ring-2 focus:ring-blue-500 transition-all duration-200"
          />
        </div>
        <Select value={signatureFilter} onValueChange={setSignatureFilter}>
          <SelectTrigger className="w-full sm:w-48 focus:ring-2 focus:ring-blue-500 transition-all duration-200">
            <SelectValue placeholder="Signature" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="signature">Signature</SelectItem>
            <SelectItem value="non-signature">Non-signature</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-full sm:w-48 focus:ring-2 focus:ring-blue-500 transition-all duration-200">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {MOVE_TYPES.map(type => (
              <SelectItem key={type} value={type}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredContent.map((item, index) => {
          const isCombo = item.contentType === 'combo';
          const inArsenal = isCombo ? true : arsenal.includes(item.id);
          const inLearning = isCombo ? false : learning.includes(item.id);
          
          return (
            <Card
              key={item.id}
              className={`group p-4 transform transition-all duration-300 border-2 animate-in fade-in slide-in-from-bottom-4 cursor-pointer ${
                theme === 'light' ? 'bg-white border-black text-black hover:shadow-lg' : 'bg-black border-white text-white hover:shadow-lg'
              } ${isCombo ? 'border-purple-500' : ''}`}
              style={{ animationDelay: `${index * 100}ms` }}
              onClick={() => setSelectedMove(item)}
            >
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <CardTitle className={`text-xl font-bold group-hover:text-blue-600 transition-colors duration-200 ${theme === 'light' ? 'text-black' : 'text-white'}`}>
                    {item.name}
                    {isCombo && <Zap className="w-4 h-4 ml-2 inline text-purple-500" />}
                  </CardTitle>
                  <div className="flex flex-col gap-1">
                    {/* Étoile signature au-dessus de l'étiquette */}
                    <button
                      className={`flex items-center justify-center w-6 h-6 ${theme === 'light' ? 'text-black' : 'text-white'}`}
                      onClick={e => {
                        e.stopPropagation();
                        if (isCombo) setComboSignature(item.id, !item.isSignature);
                        else setMoveSignature(item.id, !item.isSignature);
                      }}
                      title={item.isSignature ? 'Unmark Signature' : 'Mark Signature'}
                    >
                      <span className={`${item.isSignature ? 'text-yellow-500' : 'text-gray-400'} text-lg`}>★</span>
                      <span className="sr-only">{item.isSignature ? 'Unmark Signature' : 'Mark Signature'}</span>
                    </button>
                    
                    {isCombo ? (
                      <div className="flex flex-col gap-1">
                        <Badge className="bg-purple-100 text-purple-800 border-purple-200 font-medium">
                          Combo
                        </Badge>
                        <Badge className="bg-gray-100 text-gray-800 border-gray-200 font-medium text-xs">
                          {item.moves.length} items
                        </Badge>
                        {item.videoLink && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); window.open(item.videoLink, '_blank'); }}
                            className="text-xs px-2 py-1 text-blue-600 hover:text-blue-700"
                          >
                            <ExternalLink className="w-3 h-3 mr-1" />
                            Video
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); onEditCombo(item); }}
                          className="text-xs px-2 py-1"
                        >
                          Edit
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-1">
                        <Badge className={`${getTypeColor(item.type)} font-medium`}>
                          {item.type}
                        </Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); onEditMove(item); }}
                          className="text-xs px-2 py-1"
                        >
                          Edit
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                {!isCombo && (
                  <div className="flex gap-2 mt-2">
                    <Button
                      size="sm"
                      onClick={e => { e.stopPropagation(); inArsenal ? removeFromArsenal(item.id) : addToArsenal(item.id); }}
                      className={`${inArsenal ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'} text-white font-bold`}
                    >
                      {inArsenal ? 'Remove from Arsenal' : 'Add to Arsenal'}
                    </Button>
                    <Button
                      size="sm"
                      onClick={e => { e.stopPropagation(); inLearning ? removeFromLearning(item.id) : addToLearning(item.id); }}
                      className={`${inLearning ? 'bg-red-600 hover:bg-red-700' : 'bg-yellow-500 hover:bg-yellow-600'} text-white font-bold`}
                    >
                      {inLearning ? 'Remove from Learning' : 'Add to Learning'}
                    </Button>
                  </div>
                )}
                {/* Liste des crews pour sélection multi-partage */}
                {!item.isBasic || item.contentType !== 'move' ? (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {(getMyCrews() || []).map(crew => {
                      const checked = isContentSharedToCrew({ contentType: isCombo ? 'combo' : 'move', contentId: item.id, crewId: crew.id });
                      return (
                        <label key={crew.id} className={`flex items-center gap-2 px-2 py-1 border rounded cursor-pointer ${checked ? 'bg-green-50 border-green-300' : 'bg-white border-gray-300'}`} onClick={e => e.stopPropagation()}>
                          <input type="checkbox" checked={checked} onChange={() => toggleContentShare({ contentType: isCombo ? 'combo' : 'move', contentId: item.id, crewId: crew.id })} />
                          <span className="text-xs">{crew.name}</span>
                        </label>
                      );
                    })}
                  </div>
                ) : null}
              </CardHeader>
              <CardContent className="space-y-3">
                <p className={`text-sm leading-relaxed ${theme === 'light' ? 'text-gray-700' : 'text-gray-300'}`}>
                  {item.description}
                </p>
                {isCombo && item.moves && (
                  <div className="space-y-2">
                    <p className={`text-xs font-medium ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}`}>
                      Moves in this combo:
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {item.moves.slice(0, 3).map((move, idx) => (
                        <Badge key={idx} className={`text-xs ${getTypeColor(move.type)}`}>
                          {move.name}
                        </Badge>
                      ))}
                      {item.moves.length > 3 && (
                        <Badge className="text-xs bg-gray-100 text-gray-800 border-gray-200">
                          +{item.moves.length - 3} more
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
        </>
      )}

      {/* Modale de détail */}
      <Dialog open={!!selectedMove && !showStretch} onOpenChange={() => { setSelectedMove(null); setShowStretch(false); }}>
        <DialogContent className={`max-w-lg ${theme === 'light' ? 'bg-white border-black text-black' : 'bg-black border-white text-white'}`}>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center flex items-center justify-center gap-2">
              {selectedMove?.name}
              {selectedMove?.contentType === 'combo' && <Zap className="w-5 h-5 text-purple-500" />}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
              <div className="flex gap-2 items-center justify-between">
                <div className="flex gap-2 items-center">
                  {selectedMove?.contentType === 'combo' ? (
                    <div className="flex gap-2">
                      <Badge className="bg-purple-100 text-purple-800 border-purple-200 font-medium">Combo</Badge>
                      <Badge className="bg-gray-100 text-gray-800 border-gray-200 font-medium">
                        {selectedMove.moves.length} moves
                      </Badge>
                    </div>
                  ) : (
                    <Badge className={`${getTypeColor(selectedMove?.type)} font-medium`}>{selectedMove?.type}</Badge>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (selectedMove?.contentType === 'combo') {
                      onEditCombo(selectedMove);
                    } else {
                      onEditMove(selectedMove);
                    }
                    setSelectedMove(null);
                  }}
                  className="text-xs px-2 py-1"
                >
                  Edit
                </Button>
              </div>
              <p className={`text-sm leading-relaxed ${theme === 'light' ? 'text-gray-700' : 'text-gray-300'}`}>
                {selectedMove?.description}
              </p>
              
                             {selectedMove?.contentType === 'combo' && selectedMove?.moves && (
                 <div className="space-y-2">
                   <span className={`font-medium text-sm ${theme === 'light' ? 'text-gray-800' : 'text-gray-300'}`}>Items in this combo:</span>
                   <div className="space-y-2">
                     {selectedMove.moves.map((move, index) => (
                       <div key={move.uniqueId || move.id} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded">
                         <span className="text-sm font-medium">{index + 1}.</span>
                         <span className="text-sm">{move.name}</span>
                         {move.itemType === 'combo' ? (
                           <div className="flex gap-1">
                             <Badge className="bg-purple-100 text-purple-800 border-purple-200 text-xs">Combo</Badge>
                             <Badge className="bg-gray-100 text-gray-800 border-gray-200 text-xs">{move.moves?.length || 0} items</Badge>
                           </div>
                         ) : (
                           <Badge className={`text-xs ${getTypeColor(move.type)}`}>
                             {move.type}
                           </Badge>
                         )}
                         {move.variations && move.variations.length > 0 && (
                           <div className="flex flex-wrap gap-1">
                             {move.variations.map((variation, vIndex) => (
                               <Badge key={vIndex} className="bg-green-100 text-green-800 border-green-200 text-xs">
                                 {variation}
                               </Badge>
                             ))}
                           </div>
                         )}
                       </div>
                     ))}
                   </div>
                 </div>
               )}
              

              
              {selectedMove?.origin && (
                <div>
                  <span className={`font-medium text-sm ${theme === 'light' ? 'text-gray-800' : 'text-gray-300'}`}>Origin:</span>
                  <p className={`text-sm ${theme === 'light' ? 'text-gray-700' : 'text-gray-400'}`}>{selectedMove.origin}</p>
                </div>
              )}
              {selectedMove?.videoLink && (
                <Button
                  variant="outline"
                  size="sm"
                  className={`w-full transition-all duration-200 ${theme === 'light' ? 'bg-white text-black border-black hover:bg-gray-100' : 'bg-black text-white border-white hover:bg-gray-800'}`}
                  onClick={() => window.open(selectedMove.videoLink, '_blank')}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Watch Video
                </Button>
              )}
              
              {/* Boutons pour lancer des sessions (seulement pour les moves) */}
              {selectedMove?.contentType !== 'combo' && (
                <div className="space-y-2 mt-4">
                  <Button 
                    onClick={() => setShowConditioning(true)}
                    className="w-full bg-blue-600 text-white font-bold"
                  >
                    Start Conditioning Session
                  </Button>
                  <Button 
                    onClick={() => setShowMobility(true)}
                    className="w-full bg-purple-600 text-white font-bold"
                  >
                    Start Mobility Session
                  </Button>
                </div>
              )}
              
              {/* Learning Notes Section - only for moves */}
              {selectedMove?.contentType !== 'combo' && selectedMove && (() => {
                const learningStats = getLearningStats(selectedMove.id);
                return learningStats?.notes ? (
                  <div className="mt-4">
                    <div className="text-gray-600 dark:text-gray-400 text-xs mb-2 font-bold">📋 Learning Notes History</div>
                    <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-700 max-h-32 overflow-y-auto">
                      <div className="text-gray-800 dark:text-white text-sm space-y-2">
                        {learningStats.notes.split('\n\n').map((comment, index) => {
                          if (comment.trim() === '') return null;
                          const timestampMatch = comment.match(/^\[(.*?)\]/);
                          const timestamp = timestampMatch ? timestampMatch[1] : '';
                          const content = timestampMatch ? comment.substring(timestampMatch[0].length).trim() : comment;
                          
                          return (
                            <div key={index} className="bg-white dark:bg-gray-700 p-2 rounded border border-gray-200 dark:border-gray-600">
                              <div className="text-blue-600 dark:text-blue-300 text-xs font-mono mb-1">{timestamp}</div>
                              <div className="text-gray-800 dark:text-white text-sm">{content}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : null;
              })()}
            </div>
        </DialogContent>
      </Dialog>

      {/* Conditioning Session */}
      {showConditioning && (
        <Dialog open={showConditioning} onOpenChange={(open) => {
          setShowConditioning(open);
          if (!open) {
            setSelectedMove(null);
          }
        }}>
          <DialogContent className={`sm:max-w-2xl ${theme === 'light' ? 'bg-white border-black text-black' : 'bg-black border-white text-white'}`}>
            <ConditioningSession move={selectedMove?.name} level="beginner" onClose={() => {
              setShowConditioning(false);
              setSelectedMove(null);
            }} />
          </DialogContent>
        </Dialog>
      )}

      {/* Mobility Session */}
      {showMobility && (
        <Dialog open={showMobility} onOpenChange={(open) => {
          setShowMobility(open);
          if (!open) {
            setSelectedMove(null);
          }
        }}>
          <DialogContent className={`sm:max-w-2xl ${theme === 'light' ? 'bg-white border-black text-black' : 'bg-black border-white text-white'}`}>
            <MobilitySession move={selectedMove?.name} level="beginner" onClose={() => {
              setShowMobility(false);
              setSelectedMove(null);
            }} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default EncyclopediaView;